// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import { Icon } from '@/components/common/icons';

// ** Types
import type { TypographyProps } from '@mui/material/Typography';

export type ArrowSlideHoverCtaProps = {
	/** Ignored when `renderAsSpan` is true (parent provides the link). */
	href?: string;
	label: string;
	color?: string;
	typographyVariant?: TypographyProps['variant'];
	fontSize?: string | number;
	fontWeight?: number;
	lineHeight?: string | number;
	letterSpacing?: string | number;
	/** Use inside an outer `<Link>` / `<a>` to avoid nested anchors. */
	renderAsSpan?: boolean;
	/** When `renderAsSpan`, drives arrow animation from parent hover (e.g. whole card). */
	active?: boolean;
};

const TRANSITION = '420ms cubic-bezier(0.22, 1, 0.36, 1)';

const ArrowSlideHoverCta = ({
	href,
	label,
	color,
	typographyVariant = 'body-lg' as const,
	fontSize,
	fontWeight = 500,
	lineHeight,
	letterSpacing,
	renderAsSpan = false,
	active
}: ArrowSlideHoverCtaProps) => {
	const theme = useTheme();
	const [hovered, setHovered] = React.useState(false);

	const motionActive = renderAsSpan ? (active ?? hovered) : hovered;

	const typographySx = {
		color: color ?? theme.palette.common.white,
		fontSize,
		fontWeight,
		lineHeight,
		letterSpacing,
		position: 'relative' as const,
		display: 'inline-block',
		transform: motionActive ? 'translateX(5px)' : 'translateX(0)',
		transition: `transform ${TRANSITION}`,
		willChange: 'transform',
		'&::after': {
			content: '""',
			position: 'absolute',
			left: 0,
			right: 0,
			bottom: '-2px',
			height: '1px',
			backgroundColor: color ?? theme.palette.common.white,
			transformOrigin: 'left center',
			transform: motionActive ? 'scaleX(1)' : 'scaleX(0)',
			transition: `transform ${TRANSITION}`
		}
	};

	const inner = (
		<Box
			component="span"
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onFocus={() => setHovered(true)}
			onBlur={() => setHovered(false)}
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 2,
				cursor: 'pointer'
			}}
		>
			<Box
				component="span"
				aria-hidden="true"
				sx={{
					display: 'inline-flex',
					alignItems: 'center'
				}}
			>
				<Icon
					name="arrowSmallRight"
					aria-hidden="true"
					width={11}
					height={9}
					color={color ?? theme.palette.common.white}
				/>
			</Box>

			<Typography variant={typographyVariant} component="span" sx={typographySx}>
				{label}
			</Typography>
		</Box>
	);

	if (renderAsSpan) {
		return inner;
	}

	return (
		<Link href={href ?? '#'} passHref>
			{inner}
		</Link>
	);
};

export default ArrowSlideHoverCta;
